/**
 * Angle measurement computation.
 *
 * Computes angles formed by triplets of keypoint positions.
 */

import type { CenterlineNode } from '../../centerline';
import { subtract, dot, length as vecLength } from '../../geometry';
import type { Vec3 } from '../../types';

/**
 * Compute the angle (in degrees) formed at the vertex (middle point) of three positions.
 *
 * @param positions - Three points: [arm1, vertex, arm2]
 * @param formula - 'direct' returns the angle as-is, 'supplementary' returns 180 - angle
 */
export function computeAngle(
    positions: readonly [Vec3, Vec3, Vec3],
    formula: 'direct' | 'supplementary'
): number {
    const [pA, pVertex, pC] = positions;

    // Vectors from vertex to each arm
    const vA = subtract(pA, pVertex);
    const vC = subtract(pC, pVertex);

    const lenA = vecLength(vA);
    const lenC = vecLength(vC);

    if (lenA < 1e-9 || lenC < 1e-9) {
        return 0;
    }

    // Clamp to [-1, 1] to avoid NaN from floating point imprecision
    const cosAngle = Math.max(-1, Math.min(1, dot(vA, vC) / (lenA * lenC)));
    const angleDeg = (Math.acos(cosAngle) * 180) / Math.PI;

    return formula === 'supplementary' ? 180 - angleDeg : angleDeg;
}

/**
 * Compute the vertex angle (in degrees) from three centerline node IDs.
 *
 * Resolves each node's world position from the pool and delegates to
 * {@link computeAngle}. Returns null if any node is missing.
 *
 * @param nodeIds - [p1, vertex, p2]; the angle is measured at the vertex.
 */
export function computeAngleAtNodes(
    nodeIds: readonly [number, number, number],
    nodePool: ReadonlyMap<number, CenterlineNode>,
    formula: 'direct' | 'supplementary'
): number | null {
    const pts = nodeIds.map((id) => nodePool.get(id)?.point);
    if (pts.some((p) => !p)) return null;
    return computeAngle(pts as [Vec3, Vec3, Vec3], formula);
}

export type AngleSlot = 'p1' | 'vertex' | 'p2';

export interface AnglePreviewWedge {
    readonly vertex: Vec3;
    readonly arm1: Vec3;
    readonly arm2: Vec3 | null;
}

/**
 * Resolve the live angle-preview wedge from the placed slot positions, with the
 * cursor substituted into the slot currently being filled (`nextSlot`).
 *
 * Slot placement order is irrelevant: the vertex always tracks its own slot, so
 * placing P1 and P2 before the vertex still previews the wedge at the vertex
 * rather than at an arm. Returns null when the vertex is not yet known (and so
 * there is nothing meaningful to draw). `arm2` is null while only the vertex and
 * a single arm are known (a single-arm line).
 */
export function resolveAnglePreviewWedge(
    placed: Readonly<Record<AngleSlot, Vec3 | null>>,
    nextSlot: AngleSlot,
    cursor: Vec3
): AnglePreviewWedge | null {
    const posOf = (slot: AngleSlot): Vec3 | null => (slot === nextSlot ? cursor : placed[slot]);
    const vertex = posOf('vertex');
    const p1 = posOf('p1');
    const p2 = posOf('p2');
    const arm1 = p1 ?? p2;
    if (!vertex || !arm1) return null;
    return { vertex, arm1, arm2: p1 && p2 ? p2 : null };
}
